import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { mercuryApi } from "../api/mercuryApi";
import { Pipeline_Entity } from "../../entities/pipeline";

const pipelinesAdapter = createEntityAdapter<Pipeline_Entity>();
const initialState = pipelinesAdapter.getInitialState();

export const pipelineEndpoints = mercuryApi.injectEndpoints({
  endpoints: (build) => ({
    getPipelines: build.query<EntityState<Pipeline_Entity>, void>({
      query: () => `pipelines`,
      transformResponse: (responseData: Pipeline_Entity[]) => {
        return pipelinesAdapter.setAll(initialState, responseData);
      },
      providesTags: ["pipelines"],
    }),
    getPipeline: build.query<Pipeline_Entity, number>({
      query: (id) => `pipelines/${id}`,
      providesTags: (result, error, id) => [{ type: "pipelines", id }],
    }),
    // Modeled as a mutation (not a query) so the duplicated pipeline is never
    // cached and the pipelines list is refreshed afterwards. The backend route
    // is a side-effecting GET, so the request method stays GET.
    duplicatePipeline: build.mutation<Pipeline_Entity, number>({
      query: (id) => ({ url: `pipelines/${id}/duplicate`, method: "GET" }),
      invalidatesTags: ["pipelines"],
    }),
    createPipeline: build.mutation<Pipeline_Entity, Partial<Pipeline_Entity>>({
      query: (body) => ({
        method: "POST",
        body,
        url: `pipelines`,
      }),
      invalidatesTags: ["pipelines"],
    }),
    updatePipeline: build.mutation<
      void,
      { id: number; body: Partial<Pipeline_Entity> }
    >({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `pipelines/${args.id}`,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "pipelines", id },
        "pipelines",
      ],
    }),
    deletePipeline: build.mutation<void, number>({
      query: (id) => ({
        method: "DELETE",
        url: `pipelines/${id}`,
      }),
      invalidatesTags: ["pipelines"],
    }),
  }),
});

export const {
  useDuplicatePipelineMutation,
  useGetPipelineQuery,
  useLazyGetPipelineQuery,
  useGetPipelinesQuery,
  useCreatePipelineMutation,
  useDeletePipelineMutation,
  useUpdatePipelineMutation,
} = pipelineEndpoints;

export default pipelineEndpoints;
